import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { CircularProgress } from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import saveSuccess from '../../icons/saveSuccess.svg'

import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { theme } from '../../themes/theme'
import { BoldSmallBodyText, ErrorText, SmallBodyText } from '../styles/Text'
import { FlexColumn, FlexRow } from '../styles/Containers'

const Container = styled.div`
  position: relative;
  width: 100%;
`

const SpaceBetweenContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const DropdownContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  width: 100%;
  text-align: start;
  font-family: inherit;

  background: ${props => (props.disabled ? theme.colors.background.light : theme.colors.background.white)};
  border: ${props => `1px solid ${props.border}`};
  opacity: ${props => (props.disabled ? '0.8' : '1')};
  border-radius: ${theme.borderRadius.input};
  padding: 5px 10px;

  :hover {
    background: ${props => (props.disabled ? theme.colors.background.light : '#FAFAFA')};
    cursor: ${props => (props.disabled ? 'cursor' : 'pointer')};
  }

  :focus,
  :focus-visible {
    border: 1px solid #000;
    background: ${theme.colors.background.light};
    outline: none;
  }
`

const DropdownBlockContent = styled.div`
  display: flex;
  gap: ${props => (props.gap !== undefined ? props.gap : '5px')};
  align-items: center;
`

const DropdownSelectedOption = styled(DropdownBlockContent)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const SelectedLabelContainer = styled(FlexColumn)`
  min-width: 0;
`

const SelectedLabel = styled(BoldSmallBodyText)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const IconColorWrapper = styled(DropdownBlockContent)`
  .MuiSvgIcon-root {
    color: ${props => props.color};
  }
`

const SelectedHeading = styled(SmallBodyText)`
  text-transform: uppercase;
`

const DropdownIconWrapper = styled.div`
  .MuiSvgIcon-root {
    height: 10px;
    width: 10px;
    transform: scale(2);
    transform-origin: center;
  }
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  background: ${theme.colors.background.white};
  border: 1px solid ${theme.colors.sectionDivider};
  border-radius: ${theme.borderRadius.input};
  padding: 5px 10px;

  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  z-index: 1000;

  overflow-y: auto;
  max-height: ${props => props.maxheight};

  scrollbar-width: thin;
  scrollbar-color: rgba(58, 58, 58, 0.15) #fff;
  ::-webkit-scrollbar {
    width: 7px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(58, 58, 58, 0.15);
    border-radius: 6px;
  }
`

const CloseWrapper = styled.div`
  :hover {
    cursor: pointer;
  }

  .MuiSvgIcon-root {
    height: 11px;
    width: 11px;
    transform: scale(1.2);
    transform-origin: center;
  }
`

const OptionRow = styled(FlexRow)`
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
  border-radius: ${theme.borderRadius.input};
  background: ${props => (props.selected ? theme.colors.primaryExtraLight : 'inherit')};

  .MuiSvgIcon-root {
    color: ${props => props.color};
  }

  :hover {
    cursor: pointer;
    background: ${theme.colors.primaryLight};
  }

  :focus {
    outline: none;
    background: ${theme.colors.primaryLight};
  }
`

const SaveIcon = styled.img`
  width: 17px;
`

const Spinner = styled(CircularProgress)`
  color: ${theme.colors.primary};
`

function Dropdown({ options, heading, updateHeading, defaultValue, onSelectOption, disabled = false }) {
  const [value, setValue] = useState(defaultValue)
  const [isOpen, setIsOpen] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState()
  const [maxHeight, setMaxHeight] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const buttonRef = useRef()
  const menuRef = useRef()
  useOnClickOutside(menuRef, () => {
    setIsOpen(false)
    setFocusedIndex(null)
  })

  function toggleOpen() {
    setError(false)
    setIsOpen(prev => !prev)
  }

  function handleSelect(selected) {
    const prevValue = value

    setError(false)
    setShowSaved(false)

    if (selected !== value) {
      setIsLoading(true)
      setValue(selected)
      setIsOpen(false)

      Promise.resolve(onSelectOption(selected))
        .then(() => {
          setShowSaved(true)
        })
        .catch(e => {
          console.error('There was an error updating the dropdown option', e)
          setValue(prevValue)
          setError(true)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsOpen(false)
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setIsOpen(true)
      setFocusedIndex(0)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!isOpen) setFocusedIndex(0)
      toggleOpen()
    } else if (event.key === 'Escape') {
      setIsOpen(false)
      setFocusedIndex(null)
    }
  }

  async function handleItemKeyDown(event, index) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setFocusedIndex(prevIndex => (prevIndex < options.length - 1 ? prevIndex + 1 : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setFocusedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : options.length - 1))
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect(options[index].value)
      setFocusedIndex(null)
      buttonRef.current?.focus()
    } else if (event.key === 'Escape') {
      setIsOpen(false)
      setFocusedIndex(null)
      buttonRef.current?.focus()
    }
  }

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    if (showSaved) {
      buttonRef.current?.focus()

      const timer = setTimeout(() => {
        setShowSaved(false)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [showSaved])

  useEffect(() => {
    if (error) buttonRef.current?.focus()
  }, [error])

  useEffect(() => {
    if (isOpen && focusedIndex !== -1) {
      const focusedItem = document.getElementById(`dropdown-item-${focusedIndex}`)
      focusedItem?.focus()
    }
  }, [focusedIndex, isOpen])

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceLeft = window.innerHeight - rect.bottom - 23
      setMaxHeight(`${spaceLeft}px`)
    }
  }, [isOpen])

  const selectedOption = options.find(o => o.value === value)

  return (
    <Container ref={menuRef}>
      <DropdownContainer
        ref={buttonRef}
        border={selectedOption.color}
        onClick={() => {
          if (!(disabled || isLoading)) toggleOpen()
        }}
        disabled={disabled || isLoading}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${isOpen ? 'Close' : 'Open'} ${heading ? `${heading} ` : ''}Menu`}
        onKeyDown={handleKeyDown}>
        <DropdownSelectedOption>
          <IconColorWrapper color={selectedOption.color}>{selectedOption.icon}</IconColorWrapper>
          <SelectedLabelContainer gap="0">
            <SelectedHeading>{heading}</SelectedHeading>
            <SelectedLabel>{selectedOption.label}</SelectedLabel>
          </SelectedLabelContainer>
        </DropdownSelectedOption>
        <DropdownBlockContent gap="10px">
          {isLoading && <Spinner size={18} />}
          {showSaved && <SaveIcon src={saveSuccess} aria-label="Saved" />}
          <DropdownIconWrapper>{!isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}</DropdownIconWrapper>
        </DropdownBlockContent>
      </DropdownContainer>
      {error && <ErrorText>An error occurred, please try again.</ErrorText>}
      {isOpen && (
        <OptionsContainer role="listbox" maxheight={maxHeight}>
          <SpaceBetweenContainer>
            <SmallBodyText>{updateHeading}</SmallBodyText>
            <CloseWrapper onClick={toggleOpen} aria-label="Close Options" role="button">
              <CloseIcon />
            </CloseWrapper>
          </SpaceBetweenContainer>
          {options.map((o, index) => {
            return (
              <OptionRow
                id={`dropdown-item-${index}`}
                role="option"
                aria-label={o.label}
                key={o.value}
                color={o.color}
                selected={o.value === value}
                aria-selected={o.value === value}
                onClick={() => handleSelect(o.value)}
                onKeyDown={event => handleItemKeyDown(event, index)}
                tabIndex="1">
                {o.icon}
                <BoldSmallBodyText>{o.label}</BoldSmallBodyText>
              </OptionRow>
            )
          })}
        </OptionsContainer>
      )}
    </Container>
  )
}

Dropdown.propTypes = {
  options: PropTypes.array.isRequired,
  heading: PropTypes.string,
  updateHeading: PropTypes.string,
  defaultValue: PropTypes.string.isRequired,
  onSelectOption: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default Dropdown
